/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin, { Draggable } from "@fullcalendar/interaction";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import minMax from "dayjs/plugin/minMax";

import {
  Typography,
  Button,
  DatePicker,
  Input,
  Tag,
  Image,
  Checkbox,
  GetProp,
  Spin,
  Select,
} from "antd";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import {
  getSchedulingFormData,
  processLatLngs,
  removeLatLngs,
} from "../ducks/actions";
import {
  CalendarIcon,
  CrossIcon,
  DeleteIcon,
  // FilterIcon,
  SearchIcon,
  SelectArrow,
  TickIcon,
} from "../../../atoms/CustomIcons";
import DateNavigatorLeft from "../../../atoms/CustomIcons/DateNavigatorLeft";
import DateNavigatorRight from "../../../atoms/CustomIcons/DateNavigatorRight";
import RenderEventContentSchedule from "../Component/RenderEventContentSchedule";
import CustomDayHeader from "../Component/CustomDayHeader";
import FullPageLayout from "../../../templates/FullPageLayout";
import Header from "./Header";
// import SortingIcon from "../../../atoms/CustomIcons/SortingIcon";
import EmptyCard from "../../../atoms/EmptyCard";
import { ErrorMessage } from "../../../atoms/Toasts/ErrorMessage";
import { AxiosError } from "axios";
import { publishSchedules, unscheduleVisit } from "../ducks/services";
import {
  Coordinates,
  DriveTimeDataApiItem,
  FullCalendarEvent,
  HourData,
  PublishScheduleSchema,
} from "../ducks/types";
import {
  calculateHours,
  generateEventsWithDriveTimes,
  generateWeekendBackgroundEvents,
  getWeekPosition,
} from "../../../config/utils";
import showSpecifyVisitDurationPopUp from "../Component/SpecifyVisitDuration";
import showMultiDayShiftPopup from "../Component/ShowMultiDayShiftPopup";
import { Loading3QuartersOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import ConfirmDelete from "../../../atoms/ConfirmDelete";
import { PopUp } from "../../../atoms/PopUp";
import { SuccessMessage } from "../../../atoms/Toasts/SuccessMessage";
import AddressPop from "./AddressPop";

dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(minMax);
const antIcon = <Loading3QuartersOutlined spin />;

export default function ScheduleWork() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const MAX_HOURS_PER_DAY = 11;
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const calendarRef = useRef<any>(null);
  const [hoursData, setHoursData] = useState<HourData[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [visitEvents, setVisitEvents] = useState<FullCalendarEvent[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<FullCalendarEvent[]>([]);
  const [currentWeekRange, setCurrentWeekRange] = useState("");
  const [weekStartDate, setWeekStartDate] = useState<any>();
  const [unscheduledTasks, setUnscheduledTasks] = useState<any[]>();
  const [activeWOFilter, setActiveWOFilter] = useState<boolean>(false);
  const [activeTaskFilter, setActiveTaskFilter] = useState<boolean>(false);
  const [newChangesMade, setNewChangesMade] = useState<boolean>(false);
  const [changesLeftPop, showChangesLeftPop] = useState<boolean>(false);
  const [changesLeftFunc, addChangesLeftFunc] = useState<any>({
    nextBtn: undefined,
    prevBtn: undefined,
    crossBtn: undefined,
    currentWeek: undefined,
  });
  // const [crewYardDetail, setCrewYardDetail] = useState<any>();
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [selectedCrewValue, setSelectedCrewValue] = useState<
    string | undefined
  >(undefined);

  const [crewOptions, setCrewOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const [currentWeekStartEnd, setCurrentWeekStartEnd] = useState({
    start: dayjs().startOf("week").format("YYYY-MM-DD"),
    end: dayjs().endOf("week").format("YYYY-MM-DD"),
  });
  const schedulingFormData = useAppSelector(
    (state) => state?.schedule?.schedulingFormData
  );
  const driveTimeData = useAppSelector(
    (state) => state?.schedule?.driveTimeData?.data || []
  );

  // const driveTimeLoading = useAppSelector(
  //   (state) => state?.schedule?.driveTimeData?.loading
  // );

  const [settingsData, setSettingsData] = useState({
    workingDays: [] as string[],
    shiftStartTime: 0,
    weeklyMaxHours: 0,
  });

  const slotStartTime = "08:00:00";
  const slotEndTime = "19:00:00";
  // Auto-process drive times whenever visitEvents change (initial load, drop, or move)
  const autoDriveTimeTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!visitEvents || visitEvents.length === 0) return;

    const coordinatesToProcess: Coordinates[] = visitEvents
      .filter((event) => event.origin !== undefined && event.type !== "drive")
      .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)))
      .map((event) => event.origin as Coordinates);

    const mappedCoordinates = coordinatesToProcess.map((coord) => ({
      lat: Number(coord.lat),
      lng: Number(coord.lng),
    }));

    if (autoDriveTimeTimer.current) {
      clearTimeout(autoDriveTimeTimer.current);
    }

    autoDriveTimeTimer.current = window.setTimeout(() => {
      dispatch(removeLatLngs());

      if (mappedCoordinates.length > 1) {
        dispatch(processLatLngs({ coordinates: mappedCoordinates }));
      } else {
        setCalendarEvents(visitEvents);
      }
    }, 200);

    return () => {
      if (autoDriveTimeTimer.current) {
        clearTimeout(autoDriveTimeTimer.current);
      }
    };
  }, [visitEvents, dispatch]);

  useEffect(() => {
    if (schedulingFormData.data) {
      const crewDropOptions = schedulingFormData?.data?.crews.map((crew) => ({
        label: crew.name,
        value: crew.id,
      }));

      setCrewOptions(crewDropOptions);
    }
  }, [schedulingFormData]);

  // const triggerProcessLatLngs = () => {
  //   dispatch(removeLatLngs());
  //   if (visitEvents.length > 0) {
  //     const coordinatesToProcess: Coordinates[] = visitEvents
  //       // .filter((event) => {
  //       //   const refDate = dayjs();
  //       //   return event.start && dayjs(event.start).isSame(refDate, "day");
  //       // })
  //       .filter((event) => event.origin !== undefined)
  //       .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)))
  //       .map((event) => {
  //         if (event.origin === undefined) {
  //           throw new Error("Event origin is undefined");
  //         }
  //         return event.origin;
  //       });

  //     const mappedCoordinates = coordinatesToProcess.map((coord) => ({
  //       lat: Number(coord.lat),
  //       lng: Number(coord.lng),
  //     }));

  //     if (mappedCoordinates.length > 1) {
  //       const payload = {
  //         coordinates: mappedCoordinates,
  //       };
  //       dispatch(processLatLngs(payload));
  //     } else {
  //       setCalendarEvents(visitEvents);
  //     }
  //   }
  // };

  const calculateDailyHours = (events: FullCalendarEvent[]): HourData[] => {
    const dailyTotalMinutes: { [date: string]: number } = {};

    events.forEach((event) => {
      if (event.start) {
        const start = dayjs(event.start);
        if (start.isValid()) {
          const eventDate = start.format("YYYY-MM-DD");

          dailyTotalMinutes[eventDate] =
            (dailyTotalMinutes[eventDate] || 0) + (event?.estimated_hrs || 0);
        }
      }
    });
    const hourData: HourData[] = Object.keys(dailyTotalMinutes).map((date) => {
      const totalHours = dailyTotalMinutes[date];

      return {
        date: date,
        hours: totalHours,
      };
    });

    return hourData.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
  };

  useEffect(() => {
    if (Array.isArray(driveTimeData) && visitEvents?.length > 0) {
      const generateAndSetCalendarEvents = async () => {
        const generated = await generateEventsWithDriveTimes(
          visitEvents,
          driveTimeData as DriveTimeDataApiItem[]
        );

        const dailyHours = calculateDailyHours(generated);

        if (JSON.stringify(generated) !== JSON.stringify(calendarEvents)) {
          setCalendarEvents(generated);
        }

        if (JSON.stringify(dailyHours) !== JSON.stringify(hoursData)) {
          setHoursData(dailyHours);
        }
      };

      generateAndSetCalendarEvents();
    }
  }, [visitEvents, driveTimeData]);

  // const buildDriveLoadingPlaceholders = useCallback(
  //   (events: FullCalendarEvent[]): FullCalendarEvent[] => {
  //     // consider only real visit/task events

  //     const visits = events
  //       .filter(
  //         (e) => e.type !== "drive" && e.type !== "weekend-bg"
  //         // e.type !== "crew-yard-start" &&
  //         // e.type !== "crew-yard-end"
  //       )
  //       .filter((e) => e.start && e.end);

  //     // group by day
  //     const byDay: Record<string, FullCalendarEvent[]> = {};
  //     visits.forEach((e) => {
  //       const day = dayjs(e.start as string).format("YYYY-MM-DD");
  //       if (!byDay[day]) byDay[day] = [];
  //       byDay[day].push(e);
  //     });

  //     const loaders: FullCalendarEvent[] = [];

  //     Object.entries(byDay).forEach(([day, arr]) => {
  //       const sorted = arr.sort((a, b) =>
  //         dayjs(a.start as string).diff(dayjs(b.start as string))
  //       );

  //       for (let i = 0; i < sorted.length - 1; i++) {
  //         const prev = sorted[i];
  //         const next = sorted[i + 1];
  //         const prevEnd = dayjs(prev.end as string);
  //         const nextStart = dayjs(next.start as string);

  //         // Only add a loader if there is a gap
  //         if (prevEnd.isBefore(nextStart)) {
  //           loaders.push({
  //             id: `drive-loading-${day}-${i}`,
  //             title: "Calculating route…",
  //             start: prevEnd.toISOString(),
  //             end: nextStart.toISOString(),
  //             type: "drive-loading",
  //             estimated_hrs: 0,
  //             editable: false,
  //             startEditable: false,
  //             durationEditable: false,
  //             display: "auto",
  //             backgroundColor: "#F5F5F5",
  //             borderColor: "transparent",
  //             className: "drive-loading-event",
  //             extendedProps: {
  //               loader: true,
  //               payload: {}, // payload: {}
  //             },
  //           } as unknown as FullCalendarEvent);
  //         }
  //       }
  //     });

  //     return loaders;
  //   },
  //   []
  // );

  // When drive time API is loading (triggered by drag/drop), show loader events between visits.
  // When API completes, existing effects will replace loaders with actual drive events.
  // useEffect(() => {
  //   if (!driveTimeLoading) return;

  //   // Remove any old drive events and inject loading placeholders
  //   const noDriveEvents = visitEvents.filter((e) => e.type !== "drive");
  //   const placeholders = buildDriveLoadingPlaceholders(noDriveEvents);
  //   console.log(
  //     "placeholders ===========================================",
  //     placeholders
  //   );
  //   // Keep background/yard/visit events and add the loaders
  //   setCalendarEvents(
  //     [...noDriveEvents, ...placeholders].sort((a, b) =>
  //       dayjs(a.start as string).diff(dayjs(b.start as string))
  //     )
  //   );
  // }, [driveTimeLoading, visitEvents, buildDriveLoadingPlaceholders]);

  // useEffect(() => {
  //   // Don't override loader placeholders while drive times are being calculated
  //   if (driveTimeLoading) return;

  //   if (!Array.isArray(driveTimeData) || driveTimeData.length === 0) return;
  //   if (!visitEvents || visitEvents.length === 0) return;

  //   const generateAndSetCalendarEvents = async () => {
  //     const generated = await generateEventsWithDriveTimes(
  //       visitEvents,
  //       driveTimeData as DriveTimeDataApiItem[]
  //     );

  //     const dailyHours = calculateDailyHours(generated);

  //     // Replace loader events or update if content actually changed
  //     setCalendarEvents((prev) => {
  //       const hasLoader = prev?.some?.((e) => e.type === "drive-loading");
  //       const changed =
  //         hasLoader || JSON.stringify(prev) !== JSON.stringify(generated);
  //       return changed ? generated : prev;
  //     });

  //     setHoursData((prev) =>
  //       JSON.stringify(prev) !== JSON.stringify(dailyHours) ? dailyHours : prev
  //     );
  //   };
  //   console.log("newChangesMade...", newChangesMade);
  //   // if (newChangesMade) {
  //   //   console.log("Auto-publishing schedule after drive time calculation...");
  //   //   publishSchedule();
  //   // }
  //   generateAndSetCalendarEvents();
  // }, [
  //   visitEvents,
  //   driveTimeData,
  //   driveTimeLoading,
  //   buildDriveLoadingPlaceholders,
  // ]);

  // Debounced, drive-time-aware auto-publish
  // const autoPublishQueuedRef = useRef(false);
  // const lastPublishKeyRef = useRef<string | null>(null);
  // const debouncePublishTimerRef = useRef<number | null>(null);

  // const buildPublishKey = useCallback((events: FullCalendarEvent[]) => {
  //   // Only consider real visit events (exclude drive/background/yard markers)
  //   const compact = events
  //     .filter(
  //       (e) =>
  //         e.type !== "drive" &&
  //         e.type !== "weekend-bg" &&
  //         e.type !== "crew-yard-start" &&
  //         e.type !== "crew-yard-end"
  //     )
  //     .map((e) => ({
  //       id: e.id,
  //       start: e.start,
  //       end: e.end,
  //       property_id: e.property_id,
  //       crew_id: e.crew_id,
  //     }))
  //     // ensure stable order
  //     .sort((a, b) => (a.start || "").localeCompare(b.start || ""));
  //   return JSON.stringify(compact);
  // }, []);

  // When user makes changes, mark a publish as queued
  // useEffect(() => {
  //   if (newChangesMade) {
  //     autoPublishQueuedRef.current = true;
  //   }
  // }, [newChangesMade]);

  // // Publish only once after drive times are fully processed and calendarEvents are final
  // useEffect(() => {
  //   // nothing queued
  //   if (!autoPublishQueuedRef.current) return;

  //   // wait until drive time calculation completes
  //   if (driveTimeLoading) return;

  //   // wait until we have some events to publish
  //   if (!Array.isArray(calendarEvents) || calendarEvents.length === 0) return;

  //   const key = buildPublishKey(calendarEvents);

  //   // if this arrangement was already published, skip
  //   if (lastPublishKeyRef.current === key) {
  //     autoPublishQueuedRef.current = false;
  //     return;
  //   }

  //   // debounce to coalesce multiple quick updates into a single publish
  //   if (debouncePublishTimerRef.current) {
  //     clearTimeout(debouncePublishTimerRef.current);
  //   }
  //   debouncePublishTimerRef.current = window.setTimeout(() => {
  //     // final guard before publishing
  //     if (!driveTimeLoading && autoPublishQueuedRef.current) {
  //       lastPublishKeyRef.current = key;
  //       autoPublishQueuedRef.current = false;
  //       setNewChangesMade(false);
  //     }
  //   }, 300);

  //   return () => {
  //     if (debouncePublishTimerRef.current) {
  //       clearTimeout(debouncePublishTimerRef.current);
  //     }
  //   };
  // }, [calendarEvents, driveTimeLoading, buildPublishKey]);

  const handleDateChange = (date: any) => {
    const calendarApi = calendarRef.current.getApi();
    calendarApi.gotoDate(date.format("YYYY-MM-DD"));
  };

  useEffect(() => {
    const containerEl = document.getElementById("external-events");
    if (!containerEl) return;

    const draggable = new Draggable(containerEl, {
      itemSelector: ".fc-event-sidebar",
      eventData: (eventEl) => {
        const id = eventEl.getAttribute("data-id");
        const idsToDrop = selectedEvents.length > 0 ? selectedEvents : [id];
        const tasks = unscheduledTasks?.filter((t) => idsToDrop.includes(t.id));

        return {
          id: idsToDrop.join(","), // dummy ID
          title:
            tasks && tasks?.length > 1
              ? `${tasks.length} tasks`
              : tasks?.[0]?.property_name,
          extendedProps: {
            draggedTasks: tasks, // 🔹 must be here!
          },
        };
      },
    });

    return () => draggable.destroy();
  }, [unscheduledTasks, selectedEvents]);

  useEffect(() => {
    if (weekStartDate) {
      const payload = {
        date: dayjs(weekStartDate).format("YYYY-MM-DD"),
      };
      dispatch(getSchedulingFormData(payload));
    }
  }, [dispatch, weekStartDate]);

  useEffect(() => {
    const weekendBgEvents = generateWeekendBackgroundEvents(
      currentWeekStartEnd.start,
      currentWeekStartEnd.end
    );

    if (schedulingFormData?.data && selectedCrewValue) {
      const selectedCrew = schedulingFormData?.data?.crews?.find(
        (e) => e?.id === selectedCrewValue
      );

      const crewYard = schedulingFormData?.data?.crewyard?.find(
        (e) => e?.id === selectedCrew?.crew_yard_id
      );

      const currentCrew = schedulingFormData?.data?.schedules?.filter(
        (crew: any) => crew.crew_id === selectedCrewValue
      );

      const mappedVisits: FullCalendarEvent[] = [];
      // const noVisitPlannedEvents: FullCalendarEvent[] = [];
      if (currentCrew && currentCrew?.length > 0) {
        currentCrew?.forEach((crew: any) => {
          if (Array.isArray(crew?.visits)) {
            mappedVisits.push(
              ...crew.visits.map((visit: any) => {
                // Build a lookup map of work_order_id -> scheduled_hrs from visit.visitWorkOrders
                const woInfoMap = Array.isArray(visit?.visitWorkOrders)
                  ? new Map(
                      visit.visitWorkOrders.map((vwo: any) => [
                        // prefer explicit work_order_id, fallback to id
                        vwo.work_order_id ?? vwo.id,
                        vwo.scheduled_hrs,
                      ])
                    )
                  : new Map();

                return {
                  ...crew,
                  scheduleId: crew.id,
                  id: visit.id,
                  title: visit.property_name,
                  property_address: visit.property_address,
                  start: visit.start_time,
                  end: visit.end_time,
                  status: visit.status,
                  origin: {
                    lat: visit?.latitude,
                    lng: visit?.longitude,
                  },
                  production_manager_id:
                    crew?.production_manager_ids?.[0] ??
                    visit?.workOrders?.[0]?.production_manager_id,
                  property_id: visit?.property_id,
                  service_line_id:
                    crew?.service_line_ids?.[0] ??
                    visit?.workOrders?.[0]?.service_line_id,
                  contract_id: visit?.contract_id,
                  property_name: visit?.property_name,
                  contract_name: visit?.contract_name,
                  crew_or_contractor_name:
                    visit?.workOrders?.[0]?.crew_or_contractor_name,
                  crew_id: visit?.workOrders?.[0]?.crew_id,
                  type: "workOrder",
                  extendedProps: {
                    workOrders: [
                      // attach wo_info from visit.visitWorkOrders to each work order
                      ...((visit?.workOrders || []).map((wo: any) => ({
                        ...wo,
                        wo_info: woInfoMap.get(wo.id) ?? wo.wo_info ?? 0,
                      })) as any[]),
                      ...(visit?.tasks?.map((item: any) => ({
                        ...item,
                        type: "task",
                      })) || []),
                    ],
                    scheduled_no_of_visits:
                      visit?.workOrders?.[0]?.scheduled_no_of_visits,
                    total_no_of_visits:
                      visit?.workOrders?.[0]?.total_no_of_visits,
                  },
                };
              })
            );
          }
        });
      }

      // Add "No visit planned" placeholder events for days without any visits
      // if (
      //   currentWeekStartEnd?.start &&
      //   currentWeekStartEnd?.end &&
      //   selectedCrewValue
      // ) {
      //   const startRange = dayjs(currentWeekStartEnd.start);
      //   const endRange = dayjs(currentWeekStartEnd.end);

      //   const parseTime = (t: string) => {
      //     const [hh, mm] = t.split(":").map(Number);
      //     return { hh, mm };
      //   };
      //   const { hh: startHH, mm: startMM } = parseTime(slotStartTime);
      //   const { hh: endHH, mm: endMM } = parseTime(slotEndTime);

      //   let cursorDay = startRange;
      //   while (cursorDay.isSameOrBefore(endRange, "day")) {
      //     const dateStr = cursorDay.format("YYYY-MM-DD");
      //     const hasVisit = mappedVisits.some(
      //       (v) => dayjs(v.start).format("YYYY-MM-DD") === dateStr
      //     );
      //     if (!hasVisit) {
      //       noVisitPlannedEvents.push({
      //         id: `no-visit-planned-${dateStr}`,
      //         title: "No visit planned",
      //         start: cursorDay
      //           .hour(startHH)
      //           .minute(startMM)
      //           .second(0)
      //           .toISOString(),
      //         end: cursorDay.hour(endHH).minute(endMM).second(0).toISOString(),
      //         type: "no-visit-planned",
      //         crew_id: selectedCrewValue,
      //         estimated_hrs: 0,
      //         editable: false,
      //         startEditable: false,
      //         durationEditable: false,
      //         backgroundColor: "#F5F7F8",
      //         borderColor: "#D9E0E3",
      //         className: "no-visit-planned-event",
      //         extendedProps: {
      //           placeholder: true,
      //           skipPublish: true,
      //         },
      //       } as unknown as FullCalendarEvent);
      //     }
      //     cursorDay = cursorDay.add(1, "day");
      //   }
      //   console.log("noVisitPlannedEvents", noVisitPlannedEvents);
      // }

      // Crew yard start / end marker events (non-draggable, 30 min) for each weekday (Mon–Fri)
      const crewYardEvents: FullCalendarEvent[] = [];
      const crewYardEndEvents: FullCalendarEvent[] = [];
      if (crewYard) {
        const weekStart = dayjs(currentWeekStartEnd.start);
        const weekEnd = dayjs(currentWeekStartEnd.end);

        const parseTime = (t: string) => {
          const [hh, mm] = t.split(":").map(Number);
          return { hh, mm };
        };
        const { hh: startHH, mm: startMM } = parseTime(slotStartTime);
        const { hh: endHH, mm: endMM } = parseTime(slotEndTime);
        let cursor = weekStart;

        while (cursor.isSameOrBefore(weekEnd, "day")) {
          const dow = cursor.day(); // 0 Sun ... 6 Sat
          if (dow !== 0 && dow !== 6) {
            // start marker
            const startEventStart = cursor
              .hour(startHH)
              .minute(startMM)
              .second(0);
            const startEventEnd = startEventStart.add(30, "minute");
            //Check if mappedVisits contains any schedule on the same day using start_date field in mappedVisits

            const sameDaySchedule = mappedVisits?.find((visit) =>
              dayjs(visit.start).isSame(cursor, "day")
            );
            if (!sameDaySchedule) {
              crewYardEvents.push({
                id: `crew-yard-start-${cursor.format("YYYY-MM-DD")}`,
                title: crewYard.name,
                start: startEventStart.toISOString(),
                end: startEventEnd.toISOString(),
                type: "crew-yard-start",
                estimated_hrs: 0.5,
                crew_id: selectedCrewValue,
                // crew_or_contractor_name: selectedCrew?.crew_or_contractor_name,
                origin: {
                  lat: Number(crewYard.latitude),
                  lng: Number(crewYard.longitude),
                },
                editable: false,
                startEditable: false,
                durationEditable: false,
                extendedProps: {
                  crewYard,
                  marker: "start",
                },
              } as unknown as FullCalendarEvent);
              const endEventEnd = cursor.hour(endHH).minute(endMM).second(0);
              const endEventStart = endEventEnd.subtract(30, "minute");
              crewYardEndEvents.push({
                id: `crew-yard-end-${cursor.format("YYYY-MM-DD")}`,
                title: crewYard.name,
                start: endEventStart.toISOString(),
                end: endEventEnd.toISOString(),
                type: "crew-yard-end",
                estimated_hrs: 0.5,
                crew_id: selectedCrewValue,
                // crew_or_contractor_name: selectedCrew?.crew_or_contractor_name,
                origin: {
                  lat: Number(crewYard.latitude),
                  lng: Number(crewYard.longitude),
                },
                editable: false,
                startEditable: false,
                durationEditable: false,
                extendedProps: {
                  crewYard,
                  marker: "end",
                },
              } as unknown as FullCalendarEvent);
            } else {
              crewYardEvents.push({
                id: `crew-yard-start-${cursor.format("YYYY-MM-DD")}`,
                title:
                  sameDaySchedule.starting_crew_yard_name ||
                  sameDaySchedule?.starting_custom_location_name,
                start: startEventStart.toISOString(),
                end: startEventEnd.toISOString(),
                type: "crew-yard-start",
                estimated_hrs: 0.5,
                crew_id: selectedCrewValue,
                // crew_or_contractor_name: selectedCrew?.crew_or_contractor_name,
                origin: {
                  lat: Number(
                    sameDaySchedule.starting_crew_yard_latitude ||
                      sameDaySchedule.starting_custom_latitude
                  ),
                  lng: Number(
                    sameDaySchedule.starting_crew_yard_longitude ||
                      sameDaySchedule.starting_custom_longitude
                  ),
                },
                editable: false,
                startEditable: false,
                durationEditable: false,
                extendedProps: {
                  crewYard,
                  marker: "start",
                },
              } as unknown as FullCalendarEvent);
              const endEventEnd = cursor.hour(endHH).minute(endMM).second(0);
              const endEventStart = endEventEnd.subtract(30, "minute");
              crewYardEndEvents.push({
                id: `crew-yard-end-${cursor.format("YYYY-MM-DD")}`,
                title:
                  sameDaySchedule?.finish_point === "CUSTOM"
                    ? sameDaySchedule?.finishing_custom_location_name
                    : sameDaySchedule.finish_crew_yard_name,
                start: endEventStart.toISOString(),
                end: endEventEnd.toISOString(),
                type: "crew-yard-end",
                estimated_hrs: 0.5,
                crew_id: selectedCrewValue,
                // crew_or_contractor_name: selectedCrew?.crew_or_contractor_name,
                origin: {
                  lat: Number(
                    sameDaySchedule.finish_crew_yard_latitude ||
                      sameDaySchedule.finishing_custom_latitude
                  ),
                  lng: Number(
                    sameDaySchedule.finish_crew_yard_longitude ||
                      sameDaySchedule.finishing_custom_longitude
                  ),
                },
                editable: false,
                startEditable: false,
                durationEditable: false,
                extendedProps: {
                  crewYard,
                  marker: "end",
                },
              } as unknown as FullCalendarEvent);
            }

            // end marker (30 mins ending at slotEndTime)
          }
          cursor = cursor.add(1, "day");
        }
      }

      console.log("crewYardEvents", crewYardEndEvents);

      if (
        schedulingFormData?.data?.settings &&
        schedulingFormData?.data?.settings?.length > 0
      ) {
        const workingDaysSetting = schedulingFormData.data.settings.find(
          (setting) => setting.name === "LIST_OF_WORKING_DAYS"
        );
        const shiftStartTime = schedulingFormData.data.settings.find(
          (setting) => setting.name === "SHIFT_START_TIME"
        );

        try {
          const workingDaysArr =
            workingDaysSetting && workingDaysSetting?.value
              ? JSON?.parse(workingDaysSetting?.value)
              : [];

          setSettingsData((prevData: any) => {
            return {
              ...prevData,
              workingDays: workingDaysArr || [],
              shiftStartTime: shiftStartTime?.value,
            };
          });

          // build non-working-day background events for the current week range
          if (currentWeekStartEnd?.start && currentWeekStartEnd?.end) {
            const start = dayjs(currentWeekStartEnd.start);
            const end = dayjs(currentWeekStartEnd.end);
            const nonWorkingBgEvents: FullCalendarEvent[] = [];
            let cursor = start;

            while (cursor.isSameOrBefore(end, "day")) {
              const dayLabel = cursor.format("ddd"); // "Mon","Tue",...
              if (!workingDaysArr.includes(dayLabel)) {
                nonWorkingBgEvents.push({
                  id: `temp-bg-weekend-${cursor.format("YYYY-MM-DD")}`,
                  start: cursor.hour(8).minute(0).second(0).toISOString(),
                  end: cursor.hour(19).minute(0).second(0).toISOString(),
                  display: "background",
                  backgroundColor: "transparent",
                  type: "weekend-bg",
                  title: "Day off",
                } as unknown as FullCalendarEvent);
              }
              cursor = cursor.add(1, "day");
            }

            // weekendBgEvents is a const array created above; mutate its contents so rest of code uses the non-working events
            (weekendBgEvents as FullCalendarEvent[]).length = 0;
            (weekendBgEvents as FullCalendarEvent[]).push(
              ...nonWorkingBgEvents
            );
          }
        } catch (err) {
          // if parsing fails, keep the original weekendBgEvents and ensure settingsData fallback
          setSettingsData((prevData: any) => ({
            ...prevData,
            workingDays: [],
          }));
        }

        if (workingDaysSetting) {
          setSettingsData((prevData: any) => {
            return {
              ...prevData,
              workingDays: JSON?.parse(workingDaysSetting?.value) || [],
            };
          });
        }
      }

      const workOrders = Array.isArray(schedulingFormData?.data?.workorders)
        ? schedulingFormData?.data?.workorders
            // ?.filter(
            //   (e) => e?.crew_id === selectedCrewValue
            //   // &&
            //   //   e?.scheduled_no_of_visits < (e?.total_no_of_visits ?? 0)
            // )
            .map((wo) => ({ ...wo, type: "workOrder" }))
        : [];

      const tasks = Array.isArray(schedulingFormData?.data?.tasks)
        ? schedulingFormData?.data?.tasks.map((task) => ({
            ...task,
            type: "task",
          }))
        : [];

      const unscheduled = [...workOrders, ...tasks];

      setUnscheduledTasks(unscheduled);
      // const workOrders = Array.isArray(schedulingFormData?.data?.workorders)
      //   ? schedulingFormData?.data?.workorders?.filter(
      //       (e) =>
      //         e?.crew_id === selectedCrewValue &&
      //         e?.scheduled_no_of_visits < (e?.total_no_of_visits ?? 0)
      //     )
      //   : [];

      // const tasks = Array.isArray(schedulingFormData?.data?.tasks)
      //   ? schedulingFormData?.data?.tasks
      //   : [];
      // const unscheduled = [...workOrders, ...tasks];
      // setUnscheduledTasks(unscheduled);

      if (currentCrew && currentCrew?.length > 0) {
        if (mappedVisits) {
          setVisitEvents(() => {
            return [
              ...crewYardEvents,
              ...crewYardEndEvents,
              ...mappedVisits,
              ...weekendBgEvents,
            ];
          });
        } else {
          setVisitEvents([
            ...crewYardEvents,
            ...crewYardEndEvents,
            ...weekendBgEvents,
          ]);
        }
      } else {
        setVisitEvents([
          ...crewYardEvents,
          ...crewYardEndEvents,
          ...weekendBgEvents,
        ]);
      }
    }
  }, [schedulingFormData, selectedCrewValue]);

  console.log("calendarEvents>>", calendarEvents);

  const onChange: GetProp<typeof Checkbox.Group, "onChange"> = (
    checkedValues
  ) => {
    setSelectedEvents(checkedValues as string[]);
  };

  const handleDatesSet = (arg: any) => {
    const start = dayjs(arg.start);
    const end = dayjs(arg.end).subtract(1, "day");
    setWeekStartDate(start);
    const formatted =
      start.format("MMM D") +
      " – " +
      (start.month() === end.month()
        ? end.format("MMM D")
        : end.format("MMM D"));

    setCurrentWeekStartEnd({
      start: start.format("YYYY-MM-DD"),
      end: end.format("YYYY-MM-DD"),
    });

    setCurrentWeekRange(formatted);
  };

  const getTotalScheduledHoursForDay = (
    events: FullCalendarEvent[],
    day: string,
    excludeIds: string[] = []
  ) => {
    return events
      .filter(
        (e) =>
          dayjs(e.start).format("YYYY-MM-DD") === day &&
          !excludeIds.includes(e.id) &&
          e.type !== "drive"
      )
      .reduce((sum, e) => {
        const start = dayjs(e.start);
        const end = dayjs(e.end);
        return sum + end.diff(start, "minute") / 60;
      }, 0);
  };

  const getNextAvailableStartTime = (
    events: FullCalendarEvent[],
    day: string,
    shiftStartHour = 8,
    excludeIds: string[] = []
  ): dayjs.Dayjs => {
    const eventsOnDay = events
      .filter(
        (e) =>
          dayjs(e.start).format("YYYY-MM-DD") === day &&
          e.type !== "drive" &&
          !e?.id.includes("crew-yard-end") &&
          !excludeIds.includes(e.id)
      )
      .sort((a, b) => dayjs(a.end).diff(dayjs(b.end)));

    if (eventsOnDay.length === 0) {
      return dayjs(day).hour(shiftStartHour).minute(0).second(0);
    }
    const lastEvent = eventsOnDay[eventsOnDay.length - 1];
    return dayjs(lastEvent.end);
  };

  const splitEventAcrossMultipleDays = async (
    event: FullCalendarEvent,
    remainingHours: number,
    excludedDay: string
  ): Promise<FullCalendarEvent[] | null> => {
    const selectedDays = await showMultiDayShiftPopup(
      remainingHours,
      excludedDay
      // visitEvents,
      // MAX_HOURS_PER_DAY
    );
    if (!selectedDays || Object.keys(selectedDays).length === 0) return null;

    const newEvents: FullCalendarEvent[] = Object.entries(selectedDays).map(
      ([dayStr, hours]) => {
        const start = getNextAvailableStartTime(visitEvents, dayStr, 8);
        const end = start.add(hours, "hour");

        return {
          ...event,
          id: `temp-${event.id}-split-${dayStr}`,
          start: start.toISOString(),
          end: end.toISOString(),
          estimated_hrs: hours,
          schedule_hrs: hours,
          extendedProps: {
            ...event.extendedProps,
            workOrders: (event?.extendedProps?.workOrders || []).map(
              (wo: any) => ({
                ...wo,
                wo_info: hours,
              })
            ),
          },
        };
      }
    );

    setVisitEvents((prev) =>
      [...prev, ...newEvents].sort((a, b) =>
        dayjs(a.start).diff(dayjs(b.start))
      )
    );
    return newEvents;
  };

  const splitEventForDay = async (
    event: FullCalendarEvent,
    droppedDate: dayjs.Dayjs,
    availableHours: number,
    remainingHours: number
  ): Promise<FullCalendarEvent[] | null> => {
    // If there are available hours on the current day, create an event for it
    // and then handle remaining hours separately
    const eventsToReturn: FullCalendarEvent[] = [];
    console.log("availableHours", availableHours);
    // if (availableHours > 0) {
    //   const start = getNextAvailableStartTime(
    //     visitEvents,
    //     droppedDate.format("YYYY-MM-DD"),
    //     8
    //   );
    //   const end = start.add(availableHours, "hour");
    //   const firstPart = {
    //     ...event,
    //     start: start.toISOString(),
    //     end: end.toISOString(),
    //     estimated_hrs: availableHours,
    //   };
    //   eventsToReturn.push(firstPart);
    // }

    // If there are remaining hours, let user choose how to distribute them across multiple days
    if (remainingHours > 0) {
      const multiDayResult = await splitEventAcrossMultipleDays(
        event,
        remainingHours,
        droppedDate.format("YYYY-MM-DD")
      );
      if (!multiDayResult) {
        return null; // User cancelled
      }
      // splitEventAcrossMultipleDays already adds events to visitEvents directly
      // so we don't need to return them here
    }

    return eventsToReturn;
  };

  const mergeEventsByPropertyId = (
    events: FullCalendarEvent[],
    day: string
  ): FullCalendarEvent[] => {
    const grouped: { [propertyId: string]: FullCalendarEvent } = {};

    events.forEach((event) => {
      const eventDay = dayjs(event.start).format("YYYY-MM-DD");
      if (eventDay === day && event.property_id) {
        if (!grouped[event.property_id]) {
          grouped[event.property_id] = {
            ...event,
            extendedProps: {
              ...event.extendedProps,
              workOrders: [...(event.extendedProps?.workOrders || [])],
            },
          };
        } else {
          const prev = grouped[event.property_id];
          const newStart = dayjs.min(dayjs(prev.start), dayjs(event.start));
          const newEnd = dayjs.max(dayjs(prev.end), dayjs(event.end));
          const prevWorkOrders = prev.extendedProps?.workOrders || [];
          const currWorkOrders = event.extendedProps?.workOrders || [];
          const prevEstimatedHrs = prev.estimated_hrs || 0;
          const currEstimatedHrs = event.estimated_hrs || 0;
          grouped[event.property_id] = {
            ...prev,
            start: newStart.toISOString(),
            end: newEnd.toISOString(),
            estimated_hrs: prevEstimatedHrs + currEstimatedHrs,
            extendedProps: {
              ...prev.extendedProps,
              workOrders: [...prevWorkOrders, ...currWorkOrders],
            },
          };
        }
      } else {
        grouped[event.id + Math.random()] = event;
      }
    });

    return Object.values(grouped);
  };

  // Merge tasks into workOrders if possible when dropping events
  const mergeTasksIntoWorkOrders = (
    draggedEvent: any[],
    droppedDate: dayjs.Dayjs,
    visitEvents: FullCalendarEvent[]
  ): { mergedEvents: FullCalendarEvent[]; merged: boolean } => {
    let mergedEvents = [...visitEvents];
    let merged = false;

    // console.log("visitEvents", visitEvents);

    // Only process if there are tasks in draggedEvent
    const tasks = draggedEvent.filter((t) => t.type === "task");
    if (tasks.length === 0) {
      return { mergedEvents, merged: false };
    }

    for (const task of tasks) {
      const workOrderEvent = visitEvents.find((e) => {
        // console.log(
        //   "Checking event:",
        //   e.type !== "task",
        //   dayjs(e.start).format("YYYY-MM-DD"),
        //   droppedDate.format("YYYY-MM-DD"),
        //   Array.isArray(e?.extendedProps?.workOrders) &&
        //     e?.extendedProps?.workOrders.some(
        //       (wo: any) => wo.id === task.work_order_id
        //     )
        // );
        return (
          e.type !== "task" &&
          dayjs(e.start).format("YYYY-MM-DD") ===
            droppedDate.format("YYYY-MM-DD") &&
          Array.isArray(e?.extendedProps?.workOrders)
          // &&
          // e?.extendedProps?.workOrders.some(
          //   (wo: any) => wo.id === task.work_order_id
          // )
        );
      });

      if (workOrderEvent) {
        merged = true;
        const updatedWorkOrders = [
          ...(workOrderEvent.extendedProps?.workOrders || []),
          task,
        ];
        // Optionally, update estimated_hrs if needed
        const updatedEstimatedHrs =
          (workOrderEvent.estimated_hrs || 0) + (task.estimated_hrs || 0);

        // Replace the workOrder event with the merged one
        mergedEvents = mergedEvents.map((e) =>
          e.id === workOrderEvent.id
            ? {
                ...e,
                extendedProps: {
                  ...e.extendedProps,
                  workOrders: updatedWorkOrders,
                },
                estimated_hrs: updatedEstimatedHrs,
              }
            : e
        );
      } else {
        // No matching workOrder found, show alert and skip merging
        ErrorMessage(
          `No matching workOrder found for task "${task.name}" on this day.`
        );
        merged = false;
        break;
      }
    }

    return { mergedEvents, merged };
  };

  const handleEventReceive = useCallback(
    async (info: any) => {
      // Allow drops on today; only block strictly past days

      // Block drops when crew not selected, past time, or non-working day
      // const dropStart = dayjs(info.event.start);
      // const now = dayjs();

      // if (!selectedCrewValue) {
      //   ErrorMessage("Please select a crew before scheduling.");
      //   info.event.remove();
      //   setSelectedEvents([]);
      //   return;
      // }

      // if (
      //   dropStart.isBefore(now.startOf("day")) ||
      //   (dropStart.isSame(now, "day") && dropStart.isBefore(now))
      // ) {
      //   ErrorMessage("Cannot schedule visits in the past.");
      //   info.event.remove();
      //   setSelectedEvents([]);
      //   return;
      // }

      // if (
      //   Array.isArray(settingsData?.workingDays) &&
      //   settingsData.workingDays.length > 0
      // ) {
      //   const dayLabel = dropStart.format("ddd");
      //   if (!settingsData.workingDays.includes(dayLabel)) {
      //     ErrorMessage("Cannot schedule on a non-working day.");
      //     info.event.remove();
      //     setSelectedEvents([]);
      //     return;
      //   }
      // }

      const dropStart = dayjs(info.event.start);
      const now = dayjs();

      if (!selectedCrewValue) {
        ErrorMessage("Please select a crew before scheduling.");
        info.event.remove();
        setSelectedEvents([]);
        return;
      }

      if (dropStart.isBefore(now.startOf("day"))) {
        ErrorMessage("Cannot schedule visits in the past.");
        info.event.remove();
        setSelectedEvents([]);
        return;
      }

      if (
        Array.isArray(settingsData?.workingDays) &&
        settingsData.workingDays.length > 0
      ) {
        const dayLabel = dropStart.format("ddd");
        if (!settingsData.workingDays.includes(dayLabel)) {
          ErrorMessage("Cannot schedule on a non-working day.");
          info.event.remove();
          setSelectedEvents([]);
          return;
        }
      }

      setNewChangesMade(true);
      dispatch(removeLatLngs());
      const draggedEvent: any[] = info.event.extendedProps?.draggedTasks || [];
      const droppedDate = dayjs(info.event.start);

      console.log("Dropped tasks:", draggedEvent);

      const filterSpecifyVisitTasks = draggedEvent?.filter(
        (t) => t?.pricing_model === "time_material"
      );
      const filterNoTimeEvents = draggedEvent?.filter(
        (t) => t?.estimated_hrs === 0
      );
      let specifyTime = null;
      if (
        filterNoTimeEvents.length > 0 &&
        filterSpecifyVisitTasks.length === 0
      ) {
        specifyTime = await showSpecifyVisitDurationPopUp(filterNoTimeEvents);
      }
      if (filterSpecifyVisitTasks.length > 0) {
        specifyTime = await showSpecifyVisitDurationPopUp(
          filterSpecifyVisitTasks
        );
      }

      if (filterSpecifyVisitTasks.length > 0 && !specifyTime) {
        const nonTimeMaterialTasks = draggedEvent.filter(
          (t) => t?.pricing_model !== "time_material"
        );
        if (nonTimeMaterialTasks.length === 0) {
          info.event.remove();
          setSelectedEvents([]);
          return;
        }
        draggedEvent.length = 0;
        nonTimeMaterialTasks.forEach((t) => draggedEvent.push(t));
      }
      console.log("specifyTime", specifyTime);
      if (specifyTime && typeof specifyTime === "object") {
        Object.entries(specifyTime).forEach(([taskId, hrs]) => {
          const idx = draggedEvent.findIndex((t) => t.id === taskId);
          if (idx !== -1) {
            draggedEvent[idx].estimated_hrs = Number(hrs);
          }
        });
      }

      info.event.remove();

      if (!draggedEvent || draggedEvent.length === 0 || !droppedDate) return;

      const newEvents: FullCalendarEvent[] = [];
      const dayKey = droppedDate.format("YYYY-MM-DD");
      const cursor = getNextAvailableStartTime(
        visitEvents,
        dayKey,
        8,
        draggedEvent
      );
      const scheduledHours = getTotalScheduledHoursForDay(visitEvents, dayKey);

      console.log("scheduledHours", scheduledHours);

      if (draggedEvent.length === 1) {
        const task = draggedEvent[0];
        // Adjust task hours by crew attendance or members
        // const crewCount = (() => {
        //   try {
        //     if (!selectedCrewValue) return 1;

        //     const schedule = schedulingFormData?.data?.schedules?.find(
        //       (s: any) => s.crew_id === selectedCrewValue
        //     );

        //     console.log("schedule for crewCount", schedule);

        //     // Prefer crew attendances (optionally per-day if date exists)
        //     // if (
        //     //   Array.isArray(schedule?.crewAttendances) &&
        //     //   schedule.crewAttendances.length > 0
        //     // ) {
        //     //   const dStr = droppedDate.format("YYYY-MM-DD");
        //     //   const dayAttendances = schedule.crewAttendances.filter(
        //     //     (a: any) => {
        //     //       const presentFlag =
        //     //         a?.is_present ??
        //     //         a?.present ??
        //     //         (a?.status ? a?.status !== "ABSENT" : true);
        //     //       if (a?.date) {
        //     //         return dayjs(a.date).isSame(dStr, "day") && presentFlag;
        //     //       }
        //     //       return presentFlag;
        //     //     }
        //     //   );
        //     //   const count =
        //     //     dayAttendances.length || schedule.crewAttendances.length || 1;
        //     //   return Math.max(count, 1);
        //     // }

        //     // // Fallback to crew members (leader + members)
        //     // const crew = schedulingFormData?.data?.crews?.find(
        //     //   (c: any) => c.id === selectedCrewValue
        //     // );
        //     // const members = [
        //     //   crew?.crewLeader,
        //     //   ...(crew?.crewMembers || []),
        //     // ].filter(Boolean);
        //     // return Math.max(members.length || 1, 1);
        //   } catch {
        //     return 1;
        //   }
        // })();
        // console.log("schedule for crewCount", crewCount);
        // Divide the task duration by crew count (without breaking later logic)
        // const divider = Math.max(1, 1);
        // const baseEstimated = Number(task?.estimated_hrs ?? 0);
        // const basePerVisit = Number(task?.hrs_per_visit ?? baseEstimated);

        // // Mutate local task reference so the existing durationHours line below uses adjusted values
        // if (baseEstimated > 0) {
        //   task.estimated_hrs = baseEstimated / divider;
        // }
        // if (basePerVisit > 0) {
        //   task.hrs_per_visit = basePerVisit / divider;
        // }
        const durationHours =
          task?.total_no_of_visits > 1
            ? task.hrs_per_visit ?? 0
            : task.estimated_hrs ?? 0;

        const availableHours = Math.max(0, MAX_HOURS_PER_DAY - scheduledHours);

        if (durationHours <= availableHours) {
          const end = cursor.add(durationHours, "hour");
          newEvents.push({
            id:
              task?.total_no_of_visits > 1
                ? "temp-" + task.id + "-" + (task.scheduled_no_of_visits + 1)
                : "temp-" + task.id,
            title: task.property_name,
            start: cursor.toISOString(),
            end: end.toISOString(),
            origin: {
              lat: task?.property?.latitude,
              lng: task?.property?.longitude,
            },
            property_id: task.property_id,
            property_name: task.property_name,
            property_address: task.property_address ?? task?.property?.address,
            crew_or_contractor_name: task.crew_or_contractor_name,
            crew_id: task.crew_id,
            estimated_hrs: durationHours,
            contract_id: task?.contract_id,
            contract_name: task?.contract_name,
            service_line_id: task?.service_line_id,
            production_manager_id: task?.production_manager_id,
            type: task.type,
            extendedProps: {
              workOrders: task
                ? [
                    {
                      ...task,
                      wo_info: durationHours,
                    },
                  ]
                : [],
              scheduled_no_of_visits: (task.scheduled_no_of_visits ?? 0) + 1,
              total_no_of_visits: task.total_no_of_visits,
            },
          });
        } else if (availableHours > 0) {
          const splitParts = await splitEventForDay(
            {
              id:
                task?.total_no_of_visits > 1
                  ? "temp-" + task.id + "-" + (task.scheduled_no_of_visits + 1)
                  : "temp-" + task.id,
              title: task.property_name,
              start: cursor.toISOString(),
              end: cursor.add(durationHours, "hour").toISOString(),
              origin: {
                lat: task.property.latitude,
                lng: task.property.longitude,
              },
              property_id: task.property_id,
              property_address:
                task.property_address ?? task?.property?.address,
              property_name: task.property_name,
              crew_or_contractor_name: task.crew_or_contractor_name,
              crew_id: task.crew_id,
              estimated_hrs: durationHours,
              contract_id: task?.contract_id,
              contract_name: task?.contract_name,
              service_line_id: task?.service_line_id,
              production_manager_id: task?.production_manager_id,
              type: task.type,
              extendedProps: {
                workOrders: task ? [task] : [],
                scheduled_no_of_visits: (task.scheduled_no_of_visits ?? 0) + 1,
                total_no_of_visits: task.total_no_of_visits,
              },
            },
            cursor,
            availableHours,
            durationHours - availableHours
          );
          console.log("else if", splitParts);
          if (!splitParts) {
            setSelectedEvents([]);
            if (specifyTime && typeof specifyTime === "object") {
              Object.entries(specifyTime).forEach(([taskId]) => {
                const idx = draggedEvent.findIndex((t) => t.id === taskId);
                if (idx !== -1) {
                  draggedEvent[idx].estimated_hrs = 0;
                }
              });
            }
            return;
          } else {
            newEvents.push(...splitParts);
          }
        } else {
          const splitParts = await splitEventForDay(
            {
              id:
                task?.total_no_of_visits > 1
                  ? "temp-" + task.id + "-" + (task.scheduled_no_of_visits + 1)
                  : "temp-" + task.id,
              title: task.property_name,
              start: cursor.toISOString(),
              end: cursor.add(durationHours, "hour").toISOString(),
              origin: {
                lat: task.property.latitude,
                lng: task.property.longitude,
              },
              property_id: task.property_id,
              property_address:
                task.property_address ?? task?.property?.address,
              property_name: task.property_name,
              crew_or_contractor_name: task.crew_or_contractor_name,
              crew_id: task.crew_id,
              estimated_hrs: durationHours,
              contract_id: task?.contract_id,
              contract_name: task?.contract_name,
              service_line_id: task?.service_line_id,
              production_manager_id: task?.production_manager_id,
              type: task.type,
              extendedProps: {
                workOrders: task ? [task] : [],
                scheduled_no_of_visits: (task.scheduled_no_of_visits ?? 0) + 1,
                total_no_of_visits: task.total_no_of_visits,
              },
            },
            cursor,
            0,
            durationHours
          );
          console.log("else", splitParts);
          if (!splitParts) {
            setSelectedEvents([]);
            if (specifyTime && typeof specifyTime === "object") {
              Object.entries(specifyTime).forEach(([taskId]) => {
                const idx = draggedEvent.findIndex((t) => t.id === taskId);
                if (idx !== -1) {
                  draggedEvent[idx].estimated_hrs = 0;
                }
              });
            }
            return;
          }
          if (splitParts) {
            newEvents.push(...splitParts);
          }
        }
      } else {
        const grouped: Record<string, { groupTask: any; workOrders: any[] }> =
          draggedEvent.reduce(
            (
              acc: Record<string, { groupTask: any; workOrders: any[] }>,
              task
            ) => {
              if (!acc[task.property_id]) {
                acc[task.property_id] = {
                  groupTask: { ...task },
                  workOrders: [],
                };
              }
              acc[task.property_id].workOrders.push(task);
              return acc;
            },
            {}
          );

        for (const { groupTask, workOrders } of Object.values(grouped)) {
          const totalDurationHours = workOrders.reduce(
            (sum: number, w: any) =>
              sum +
              (w.total_no_of_visits > 1
                ? w.hrs_per_visit ?? 0
                : w.estimated_hrs ?? 0),
            0
          );

          const availableHours = Math.max(
            0,
            MAX_HOURS_PER_DAY - scheduledHours
          );

          if (totalDurationHours <= availableHours) {
            console.log("totalDurationHours", totalDurationHours);
            const end = cursor.add(totalDurationHours, "hour");
            newEvents.push({
              id: `temp-${groupTask.property_id}-${Date.now()}`,
              title: groupTask.property_name,
              start: cursor.toISOString(),
              end: end.toISOString(),
              origin: {
                lat: groupTask.property.latitude,
                lng: groupTask.property.longitude,
              },
              property_id: groupTask.property_id,
              property_address:
                groupTask.property_address ?? groupTask?.property?.address,
              property_name: groupTask.property_name,
              crew_or_contractor_name: groupTask.crew_or_contractor_name,
              crew_id: groupTask.crew_id,
              contract_id: groupTask.contract_id,
              contract_name: groupTask.contract_name,
              service_line_id: groupTask.service_line_id,
              production_manager_id: groupTask.production_manager_id,
              estimated_hrs: totalDurationHours,
              type: groupTask.type,
              extendedProps: {
                workOrders: workOrders,
              },
            });
          }
          // else if (availableHours > 0) {
          //   const splitParts = await splitEventForDay(
          //     {
          //       id: `temp-${groupTask.property_id}-${Date.now()}`,
          //       title: groupTask.property_name,
          //       start: cursor.toISOString(),
          //       end: cursor.add(totalDurationHours, "hour").toISOString(),
          //       origin: {
          //         lat: groupTask.property.latitude,
          //         lng: groupTask.property.longitude,
          //       },
          //       property_id: groupTask.property_id,
          //       property_address:
          //         groupTask.property_address ?? groupTask?.property?.address,
          //       property_name: groupTask.property_name,
          //       crew_or_contractor_name: groupTask.crew_or_contractor_name,
          //       crew_id: groupTask.crew_id,
          //       contract_id: groupTask.contract_id,
          //       contract_name: groupTask.contract_name,
          //       service_line_id: groupTask.service_line_id,
          //       production_manager_id: groupTask.production_manager_id,
          //       type: groupTask.type,
          //       estimated_hrs: totalDurationHours,
          //       extendedProps: {
          //         workOrders: workOrders,
          //       },
          //     },
          //     cursor,
          //     availableHours,
          //     totalDurationHours - availableHours
          //   );
          //   if (splitParts) {
          //     newEvents.push(...splitParts);
          //   }
          // }
          else {
            const splitParts = await splitEventForDay(
              {
                id: `temp-${groupTask.property_id}-${Date.now()}`,
                title: groupTask.property_name,
                start: cursor.toISOString(),
                end: cursor.add(totalDurationHours, "hour").toISOString(),
                origin: {
                  lat: groupTask.property.latitude,
                  lng: groupTask.property.longitude,
                },
                property_id: groupTask.property_id,
                property_address:
                  groupTask.property_address ?? groupTask?.property?.address,
                property_name: groupTask.property_name,
                crew_or_contractor_name: groupTask.crew_or_contractor_name,
                crew_id: groupTask.crew_id,
                contract_id: groupTask.contract_id,
                contract_name: groupTask.contract_name,
                service_line_id: groupTask.service_line_id,
                production_manager_id: groupTask.production_manager_id,
                type: groupTask.type,
                estimated_hrs: totalDurationHours,
                extendedProps: {
                  workOrders: workOrders,
                },
              },
              cursor,
              0,
              totalDurationHours
            );
            if (splitParts) {
              newEvents.push(...splitParts);
            }
          }
        }
      }

      const { mergedEvents, merged } = mergeTasksIntoWorkOrders(
        draggedEvent,
        droppedDate,
        visitEvents
      );
      if (merged) {
        setVisitEvents(mergedEvents);
        setUnscheduledTasks((prev) =>
          prev?.filter((t) => !draggedEvent.some((sel) => sel.id === t.id))
        );
        setSelectedEvents([]);
        return;
      }

      const newFilteredEvents = newEvents.filter((ne) => ne?.type !== "task");

      setVisitEvents((prev) => {
        const day = droppedDate.format("YYYY-MM-DD");
        const combined = [...prev, ...newFilteredEvents];
        return mergeEventsByPropertyId(combined, day).sort((a, b) =>
          dayjs(a.start).diff(dayjs(b.start))
        );
      });

      setUnscheduledTasks((prev) =>
        prev
          ?.map((t) => {
            const dropped = draggedEvent.find((sel) => sel.id === t.id);
            if (!dropped) return t;

            const newScheduled = (t.scheduled_no_of_visits ?? 0) + 1;
            if (newScheduled >= t.total_no_of_visits) {
              return null;
            }

            return {
              ...t,
              scheduled_no_of_visits: newScheduled,
            };
          })
          .filter(Boolean)
      );

      setSelectedEvents([]);
    },
    [visitEvents]
  );

  const handleEventDrop = useCallback(
    (info: any) => {
      setNewChangesMade(true);
      dispatch(removeLatLngs());
      const { delta, event } = info;

      if (selectedEvents.length > 1) {
        const movedEvents = calendarRef.current.getApi().getEvents();
        movedEvents.forEach((e: any) => {
          if (selectedEvents.includes(e.id) && e.id !== event.id) {
            e.setStart(e.start && dayjs(e.start).add(delta).toDate());
            e.setEnd(e.end && dayjs(e.end).add(delta).toDate());
          }
        });
      }

      const movedEventId = event.id;
      const dayKey = dayjs(event.start).format("YYYY-MM-DD");
      const durationMin = dayjs(event.end).diff(dayjs(event.start), "minute");

      // Exclude the moved event itself when finding the next available start time
      const nextStart = getNextAvailableStartTime(visitEvents, dayKey, 8, [
        event.id,
      ]);
      const snappedStart = nextStart;
      const snappedEnd = nextStart.add(durationMin, "minute");

      // Update the event instance so downstream logic uses the snapped times
      event.setStart(snappedStart.toDate());
      event.setEnd(snappedEnd.toDate());
      const newStart = dayjs(event.start);
      const newEnd = dayjs(event.end);
      const newDate = newStart.format("YYYY-MM-DD");
      console.log(
        "cursor after drop",
        durationMin,
        dayjs(newStart).format("hh:mm:ss"),
        dayjs(newEnd).format("hh:mm:ss"),
        dayjs(newDate).format("YYYY-MM-DD hh:mm:ss")
      );

      const oldEvent = visitEvents.find((e) => e.id === movedEventId);
      if (!oldEvent) return;

      const originalDate = dayjs(oldEvent.start).format("YYYY-MM-DD");

      const updatedMovedEvent: FullCalendarEvent = {
        ...oldEvent,
        start: newStart.toISOString(),
        end: newEnd.toISOString(),
      };

      const otherEvents = visitEvents.filter((e) => e.id !== movedEventId);

      console.log("originalDate", originalDate);

      let adjustedEvents: FullCalendarEvent[] = [];

      if (originalDate === newDate) {
        adjustedEvents = otherEvents;
      } else {
        const oldDayEvents = otherEvents.filter(
          (e) => dayjs(e.start).format("YYYY-MM-DD") === originalDate
        );
        // const eventExceptYard = oldDayEvents.filter(
        //   (e) => e.type !== "crew-yard-end" && e.type !== "crew-yard-start"
        // );
        // const eventOnlyYard = oldDayEvents.filter(
        //   (e) => e.type === "crew-yard-end" || e.type === "crew-yard-start"
        // );
        // console.log("oldDayEvents", eventOnlyYard, eventExceptYard);
        // // Adjust remaining events on the original day (compress to remove gaps)
        // // Start from crew-yard-start end if present, otherwise from slot start time
        // const yardStartEvent = oldDayEvents.find(
        //   (e) => e.type === "crew-yard-start"
        // );
        // const [slotHH = 8, slotMM = 0] = (slotStartTime || "08:00:00")
        //   .split(":")
        //   .map((v) => Number(v));
        // let oldCursor = yardStartEvent
        //   ? dayjs(yardStartEvent.end)
        //   : dayjs(originalDate).hour(slotHH).minute(slotMM).second(0);

        // // Only non-yard, non-background events should be adjusted
        // const adjustableOldDayEvents = eventExceptYard
        //   .filter((e) => e.type !== "weekend-bg" && e.display !== "background")
        //   .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

        // const adjustedOldDay = adjustableOldDayEvents.map((ev) => {
        //   const durationMinOld = dayjs(ev.end).diff(dayjs(ev.start), "minute");
        //   const newStartTimeOld = oldCursor;
        //   const newEndTimeOld = newStartTimeOld.add(durationMinOld, "minute");
        //   oldCursor = newEndTimeOld;
        //   return {
        //     ...ev,
        //     start: newStartTimeOld.toISOString(),
        //     end: newEndTimeOld.toISOString(),
        //   };
        // });

        // // Include adjusted old-day events along with yard markers for that day
        // eventOnlyYard.push(...adjustedOldDay);
        const newDayEvents = otherEvents
          .filter((e) => dayjs(e.start).format("YYYY-MM-DD") === newDate)
          .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

        let newCursor = newEnd;
        const adjustedNewDay = newDayEvents.map((ev) => {
          if (ev.type === "crew-yard-end") return ev;
          if (dayjs(ev.start).isAfter(newStart)) {
            const duration = dayjs(ev.end).diff(dayjs(ev.start), "minute");
            const newStartTime = newCursor;
            const newEndTime = newStartTime.add(duration, "minute");
            newCursor = newEndTime;
            return {
              ...ev,
              start: newStartTime.toISOString(),
              end: newEndTime.toISOString(),
            };
          }
          if (dayjs(ev.end).isAfter(newCursor)) {
            newCursor = dayjs(ev.end);
          }
          return ev;
        });

        const untouched = otherEvents.filter(
          (e) =>
            dayjs(e.start).format("YYYY-MM-DD") !== originalDate &&
            dayjs(e.start).format("YYYY-MM-DD") !== newDate
        );

        adjustedEvents = [...oldDayEvents, ...adjustedNewDay, ...untouched];
      }

      const merged = mergeEventsByPropertyId(
        [...adjustedEvents, updatedMovedEvent],
        newDate
      ).sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));
      console.log("merged", merged);
      setVisitEvents(merged);
    },
    [visitEvents, selectedEvents]
  );

  const publishSchedule = () => {
    console.log("Publishing schedule...", calendarEvents);
    setLoading(true);
    const payload: PublishScheduleSchema[] = Object.values(
      calendarEvents.reduce((acc: any, item) => {
        if (
          item.type === "drive" ||
          item.type === "weekend-bg" ||
          item.type === "crew-yard-start" ||
          item.type === "crew-yard-end"
        )
          return acc;

        const date = dayjs(item.start).format("YYYY-MM-DD");
        const key = `${date}-${item.crew_id}`;

        if (!acc[key]) {
          acc[key] = {
            date,
            id: item.scheduleId,
            crew_id: selectedCrewValue,
            crew_name: schedulingFormData?.data?.crews?.find(
              (e) => e?.id === selectedCrewValue
            )?.name,
            service_lines_ids: [],
            production_manager_ids: [],
            visit: [],
            starting_point: "CREW_YARD",
            finish_point: "CREW_YARD",
            starting_point_duration: 0.5,
            finish_point_duration: 0.5,
            starting_crew_yard_id: schedulingFormData?.data?.crewyard?.find(
              (e) => e?.id === selectedCrewValue
            )?.id,
            finish_crew_yard_id: schedulingFormData?.data?.crewyard?.find(
              (e) => e?.id === selectedCrewValue
            )?.id,
            starting_crew_yard_name: schedulingFormData?.data?.crewyard?.find(
              (e) => e?.id === selectedCrewValue
            )?.name,
            finish_crew_yard_name: schedulingFormData?.data?.crewyard?.find(
              (e) => e?.id === selectedCrewValue
            )?.name,
            starting_crew_yard_latitude: "24.9002608",
            starting_crew_yard_longitude: "67.0732217",
            finish_crew_yard_latitude: "24.9002608",
            finish_crew_yard_longitude: "67.0732217",
          };
        }
        console.log("item...", item);
        const wo_info: Record<string, string> = {};

        item?.extendedProps?.workOrders?.map((wo: any) => {
          wo_info[wo.id] = wo.wo_info;
        });

        acc[key].visit.push({
          id: item.id.includes("temp-") ? undefined : item.id,
          property_id: item.property_id,
          property_name: item.property_name,
          contract_id: item.contract_id ?? null,
          contract_name: item.contract_name ?? null,
          start_time: item.start,
          end_time: item.end,
          // estimated_hrs: item?.estimated_hrs,
          duration_in_hrs:
            dayjs(item.end).diff(dayjs(item.start), "minute") / 60,
          index: acc[key].visit.length + 1,
          status: item.status || "SCHEDULED",
          wo_info,
          task_ids:
            item?.extendedProps?.workOrders &&
            item?.extendedProps?.workOrders?.length > 0
              ? item?.extendedProps?.workOrders
                  .filter((wo: any) => wo.type === "task")
                  .map((wo: any) => wo.id)
              : item.type === "task"
              ? [item.id]
              : [],
          work_order_ids:
            item?.extendedProps?.workOrders &&
            item?.extendedProps?.workOrders?.length > 0
              ? item?.extendedProps?.workOrders
                  .filter((wo: any) => wo.type !== "task")
                  .map((wo: any) => wo.id)
              : item.type !== "task"
              ? [item.id]
              : [],
        });

        if (item.service_line_id) {
          acc[key].service_lines_ids.push(item.service_line_id);
        } else if (
          item.service_lines_ids &&
          Array.isArray(item.service_lines_ids)
        ) {
          acc[key].service_lines_ids.push(...item.service_lines_ids);
        }
        if (item.production_manager_id) {
          acc[key].production_manager_ids.push(item.production_manager_id);
        } else if (
          item.production_manager_ids &&
          Array.isArray(item.production_manager_ids)
        ) {
          acc[key].production_manager_ids.push(...item.production_manager_ids);
        }

        return acc;
      }, {})
    ).map((group: any) => ({
      ...group,
      service_lines_ids: [...new Set(group.service_lines_ids)], // ✅ unique
      production_manager_ids: [...new Set(group.production_manager_ids)], // ✅ unique
    }));
    console.log("Publish payload", payload);
    publishSchedules(payload ?? [])
      .then(() => {
        setLoading(false);
        SuccessMessage("Schedule published");
        setNewChangesMade(false);
        navigate(`/scheduling/${companyId}`);
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const weekPosition = getWeekPosition(
    currentWeekStartEnd.start,
    currentWeekStartEnd.end
  );

  let crewData: any[] = [];
  if (
    schedulingFormData?.data?.schedules &&
    schedulingFormData?.data?.schedules?.length > 0
  ) {
    const filterItem = schedulingFormData?.data?.schedules?.find(
      (schedule) => schedule.crew_id === selectedCrewValue
    );

    if (
      filterItem?.crewAttendances &&
      filterItem?.crewAttendances?.length > 0
    ) {
      crewData = filterItem?.crewAttendances;
    } else {
      const filterCrew = schedulingFormData?.data?.crews?.find(
        (crew) => crew?.id === selectedCrewValue
      );

      crewData = filterCrew
        ? [filterCrew?.crewLeader, ...(filterCrew?.crewMembers ?? [])]
        : [];
    }
  } else {
    const filterCrew = schedulingFormData?.data?.crews?.find(
      (crew) => crew?.id === selectedCrewValue
    );

    const leader = filterCrew?.crewLeader
      ? [{ ...filterCrew?.crewLeader, type: "leader" }]
      : [];

    crewData = filterCrew
      ? [...leader, ...(filterCrew?.crewMembers ?? [])]
      : [];
  }

  const filterCrew = schedulingFormData?.data?.crews?.find(
    (crew) => crew?.id === selectedCrewValue
  );

  const crewArrayForCalendar = {
    crewLeader: { ...filterCrew?.crewLeader, type: "leader" },
    crewMembers: filterCrew?.crewMembers ?? [],
  };

  const leftPopUp = {
    visibility: changesLeftPop,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => showChangesLeftPop(false)}
        heading="Changes not published"
        paragraph="You have unpublished changes. Are you sure you want to leave without publishing?"
        isArchivePopup
        btnText="Publish schedule"
        secondBtnText="Discard & leave"
        secondBtnApi={() => {
          if (changesLeftFunc?.crossBtn) {
            navigate(`/scheduling/${companyId}`);
          } else if (changesLeftFunc?.prevBtn) {
            calendarRef.current.getApi().prev();
          } else if (changesLeftFunc?.nextBtn) {
            calendarRef.current.getApi().next();
          } else if (changesLeftFunc?.currentWeek) {
            const api = calendarRef.current?.getApi?.();
            if (api) api.today();
          }
          showChangesLeftPop(false);
          setNewChangesMade(false);
          addChangesLeftFunc({
            nextBtn: false,
            crossBtn: false,
            prevBtn: false,
            currentWeek: false,
          });
        }}
        btnApi={() => {
          publishSchedule();
        }}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => showChangesLeftPop(false),
  };

  // console.log("crewData", crewData);
  // console.log("calendarEvents", calendarEvents, unscheduledTasks);
  // console.log("settingsData", settingsData);
  const [selectedVisitIds, setSelectedVisitIds] = useState<any>([]);

  function compactEvents(
    events: FullCalendarEvent[],
    selectedIds: string[]
  ): FullCalendarEvent[] {
    if (!events || events.length === 0) return [];

    // sort by original start time (ascending)
    const sorted = [...events].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    );

    // anchor is the original earliest start (schedule should begin here)
    let cursor = new Date(sorted[0].start).getTime();

    // keep only non-selected events in chronological order and reassign times
    const remaining = sorted.filter((e) => !selectedIds.includes(e.id));

    return remaining.map((e) => {
      const duration = new Date(e.end).getTime() - new Date(e.start).getTime();
      const newStart = new Date(cursor);
      const newEnd = new Date(cursor + duration);
      cursor += duration;
      return { ...e, start: newStart.toISOString(), end: newEnd.toISOString() };
    });
  }

  const unScheduleVisitsFnc = () => {
    const ids = (selectedVisitIds || []).filter(Boolean);

    const oneevent = calendarEvents.find(
      (item) => item.id === selectedVisitIds[0]
    );
    console.log("JSON.stringify(oneevent)");
    console.log("JSON.stringify(oneevent)");
    console.log(selectedVisitIds);
    console.log(JSON.stringify(oneevent));
    console.log(JSON.stringify(calendarEvents));

    if (ids.length === 0 || !oneevent) return;
    const todaysEvents = calendarEvents.filter(
      (e) =>
        e.start &&
        dayjs(e.start).isSame(oneevent.date, "day") &&
        e.type !== "weekend-bg" &&
        e.type !== "drive" &&
        e.type !== "crew-yard-start" &&
        e.type !== "crew-yard-end"
    );
    console.log("JSON.stringify(todaysEvents)");
    console.log(JSON.stringify(todaysEvents));
    const compactedEvents = compactEvents(todaysEvents, ids);
    const freshEvents = calendarEvents.filter(
      (e) => selectedVisitIds.includes(e.id) === false
    );
    for (const element of compactedEvents) {
      const index = freshEvents.findIndex((e) => e.id === element.id);
      if (index !== -1) {
        freshEvents[index] = {
          ...freshEvents[index],
          start: element.start,
          end: element.end,
        };
      }
    }
    setVisitEvents(freshEvents);
    console.log(JSON.stringify(freshEvents));

    // return;
    // const newEvents: FullCalendarEvent[] = todaysEvents.map((item) => {
    //   const start = getNextAvailableStartTime(todaysEvents, dayjs(item.date).format("YYYY-MM-DD"), 8);
    //   const end = start.add(hours, "hour");

    //   // return {
    //   //   ...event,
    //   //   id: `temp-${event.id}-split-${dayStr}`,
    //   //   start: start.toISOString(),
    //   //   end: end.toISOString(),
    //   //   estimated_hrs: hours,
    //   // };
    // });
    // const start = getNextAvailableStartTime(todaysEvents, dayjs(oneevent.date).format("YYYY-MM-DD"), 8);
    // console.log(JSON.stringify(start));

    setLoading(true);

    try {
      // Partition ids
      const tempIds = ids.filter((id: string) =>
        String(id).startsWith("temp-")
      );
      const apiIds = ids.filter(
        (id: string) => !String(id).startsWith("temp-")
      );

      // Helper to collect work items (tasks/workorders) from events -> to return to unscheduled list
      const collectWorkItems = (eventsToProcess: FullCalendarEvent[]) =>
        eventsToProcess.flatMap(
          (e) => e?.extendedProps?.workOrders || []
        ) as any[];

      // 1) Handle locally created visits (temp-*)
      if (tempIds.length > 0) {
        setNewChangesMade(true);
        const tempEvents = calendarEvents.filter((e) => tempIds.includes(e.id));
        const workItemsToReturn = collectWorkItems(tempEvents);

        // Remove from visitEvents so drive times recalc properly
        setVisitEvents((prev) => prev.filter((e) => !tempIds.includes(e.id)));
        dispatch(removeLatLngs());

        // Return items back to unscheduledTasks (decrement scheduled_no_of_visits by 1)
        if (workItemsToReturn.length > 0) {
          setUnscheduledTasks((prev) => {
            const map = new Map<string, any>(
              (prev || []).map((item: any) => [String(item.id), item])
            );
            workItemsToReturn.forEach((item: any) => {
              const key = String(item.id);
              if (map.has(key)) {
                const existing = map.get(key);
                map.set(key, {
                  ...existing,
                  scheduled_no_of_visits: Math.max(
                    0,
                    (existing?.scheduled_no_of_visits ?? 0) - 1
                  ),
                });
                setLoading(false);
                setSelectedVisitIds((prevIds: any[]) =>
                  prevIds.filter((id) => id !== item.id)
                );
              } else {
                map.set(key, {
                  ...item,
                  scheduled_no_of_visits: Math.max(
                    0,
                    (item?.scheduled_no_of_visits ?? 0) - 1
                  ),
                });
                setSelectedVisitIds((prevIds: any[]) =>
                  prevIds.filter((id) => id !== item.id)
                );
                setLoading(false);
              }
            });
            return Array.from(map.values());
          });
          setSelectedVisitIds((prevIds: any[]) =>
            prevIds.filter((id) => !tempIds.includes(id))
          );
        }
      } else {
        setNewChangesMade(false);
      }

      // 2) Handle visits that already exist in BE (non temp ids)
      if (apiIds.length > 0) {
        console.log("Un-scheduling visits via API for ids:", apiIds);
        setLoading(false);
        unscheduleVisit(apiIds)
          .then(() => {
            // On success, remove from visitEvents
            setVisitEvents((prev) =>
              prev.filter((e) => !apiIds.includes(e.id))
            );
            setSelectedVisitIds((prevIds: any[]) =>
              prevIds.filter((id) => !apiIds.includes(id))
            );
            dispatch(removeLatLngs());
            const payload = {
              date: dayjs(weekStartDate).format("YYYY-MM-DD"),
            };
            dispatch(getSchedulingFormData(payload));
            setNewChangesMade(false);
            setLoading(false);
          })
          .catch((e) => {
            if (e instanceof AxiosError) {
              ErrorMessage(
                e?.response?.data?.error?.message ?? "Something went wrong"
              );
              setLoading(false);
            } else {
              ErrorMessage("Something went wrong");
              setLoading(false);
            }
          });
      }
    } catch (error) {
      console.error("Error un-scheduling visits:", error);
      ErrorMessage("Failed to un-schedule visits. Please try again.");
    }
  };

  const discardChanges = () => {
    setLoading(true);
    dispatch(removeLatLngs());
    const payload = {
      date: dayjs(weekStartDate).format("YYYY-MM-DD"),
    };
    dispatch(getSchedulingFormData(payload));
    setNewChangesMade(false);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  console.log("newChangesMade", settingsData?.shiftStartTime);

  return (
    <Spin indicator={antIcon} size="large" spinning={false}>
      <FullPageLayout loading={loading || schedulingFormData?.loading}>
        <div className="scheduleWorkContainer">
          <Header
            schedulingFormData={schedulingFormData?.data}
            setSelectedCrewValue={setSelectedCrewValue}
            selectedCrewValue={selectedCrewValue}
            publishSchedule={publishSchedule}
            setCalendarEvents={setCalendarEvents}
            setVisitEvents={setVisitEvents}
            setHoursData={setHoursData}
            newChangesMade={newChangesMade}
            showChangesLeftPop={showChangesLeftPop}
            addChangesLeftFunc={addChangesLeftFunc}
          />
          <div className="scheduleWorkContent">
            <div id="external-events" className="external-event-sidebar">
              <div className="sidebar_wrapper">
                <Typography.Title level={3} style={{ marginBottom: "20px" }}>
                  Work to schedule
                </Typography.Title>
                <div
                  className="filterSearch"
                  style={{
                    display: "flex",
                    gap: "5px",
                    marginBottom: "8px",
                  }}
                >
                  <Input
                    prefix={<SearchIcon />}
                    placeholder="Search WOs or tasks"
                    allowClear
                    onChange={(e) => {
                      const value = e.target.value?.toLowerCase() || "";
                      console.log("searching for", value);
                      if (!value) {
                        const woArrays =
                          schedulingFormData?.data?.workorders?.map((wo) => ({
                            ...wo,
                            type: "workOrder",
                          })) || [];
                        const taskArrays =
                          schedulingFormData?.data?.tasks?.map((task) => ({
                            ...task,
                            type: "task",
                          })) || [];
                        setUnscheduledTasks([
                          ...(woArrays ?? []),
                          ...(taskArrays ?? []),
                        ]);
                      } else {
                        setUnscheduledTasks((prev) =>
                          (prev || []).filter(
                            (task) =>
                              task.name?.toLowerCase().includes(value) ||
                              task.property_name?.toLowerCase().includes(value)
                          )
                        );
                      }
                    }}
                  />

                  {/* <div
                    className="rightBtns"
                    style={{ display: "flex", gap: "5px" }}
                  >
                    <Button
                      style={{ height: "36px", width: "36px" }}
                      icon={<SortingIcon />}
                    />
                    <Button
                      style={{ height: "36px", width: "36px" }}
                      icon={<FilterIcon />}
                    />
                  </div> */}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    textAlign: "center",
                    marginBottom: "12px",
                    borderBottom: "1px solid #EFF2F3",
                    borderTop: "1px solid #eff2f3",
                    padding: "8px 9px",
                    gap: "5px",
                  }}
                >
                  <Button
                    type="text"
                    className={`unscheduled-count ${
                      activeWOFilter ? "activeFilter" : ""
                    }`}
                    onClick={() => {
                      setActiveWOFilter(!activeWOFilter);
                      setActiveTaskFilter(false);
                    }}
                    style={{ width: "55% !important" }}
                  >
                    <span style={{ fontWeight: "600" }}>
                      {
                        unscheduledTasks?.filter(
                          (task) => task.type === "workOrder"
                        )?.length
                      }
                    </span>

                    <span style={{ marginRight: "10px" }}>
                      {unscheduledTasks &&
                      unscheduledTasks?.filter(
                        (task) => task.type === "workOrder"
                      )?.length > 1
                        ? "work orders"
                        : "work order"}
                    </span>
                    {activeWOFilter && (
                      <span
                        style={{
                          position: "absolute",
                          right: "5px",
                          top: "0px",
                          height: "28px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <CrossIcon />
                      </span>
                    )}
                  </Button>
                  <Button
                    type="text"
                    className={`unscheduled-count ${
                      activeTaskFilter ? "activeFilter" : ""
                    }`}
                    onClick={() => {
                      setActiveWOFilter(false);
                      setActiveTaskFilter(!activeTaskFilter);
                    }}
                    style={{ width: "45% !important" }}
                  >
                    <span style={{ fontWeight: "600" }}>
                      {
                        unscheduledTasks?.filter((task) => task.type === "task")
                          ?.length
                      }
                    </span>
                    <span style={{ marginRight: "10px" }}>
                      {unscheduledTasks &&
                      unscheduledTasks?.filter((task) => task.type === "task")
                        ?.length > 1
                        ? "tasks"
                        : "task"}
                    </span>
                    {activeTaskFilter && (
                      <span
                        style={{
                          position: "absolute",
                          right: "5px",
                          top: "0px",
                          height: "28px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <CrossIcon />
                      </span>
                    )}
                  </Button>
                </div>

                {unscheduledTasks?.length === 0 ? (
                  <div className="schedulerEmpty">
                    <EmptyCard
                      image="/images/empty-icons/no-archived.svg"
                      title="No work orders or tasks for this crew"
                      description="Work orders appear here when the crew is assigned to a contract.."
                      imageHeight={66}
                    />
                  </div>
                ) : (
                  <Checkbox.Group
                    style={{ width: "100%" }}
                    onChange={onChange}
                    className="checkbox-group-unschedule"
                    value={selectedEvents}
                  >
                    {(activeWOFilter
                      ? unscheduledTasks?.filter(
                          (task) => task.type === "workOrder"
                        )
                      : activeTaskFilter
                      ? unscheduledTasks?.filter((task) => task.type === "task")
                      : unscheduledTasks
                    )?.map((task) => (
                      <AddressPop key={task.id} task={task} />
                    ))}
                  </Checkbox.Group>
                )}
                {/* {unscheduledTasks && unscheduledTasks.length > 0 && (
                  <div className="buttonWrapper">
                    <Button onClick={autoSchedule} loading={isAutoScheduling}>
                      {isAutoScheduling ? "Scheduling..." : "Auto schedule"}
                    </Button>
                  </div>
                )} */}
              </div>
            </div>

            <div className="fullcalendar-wrapper">
              <div
                className="scheduleHeader calendarHeader"
                style={{ padding: "12px 12px 4px" }}
              >
                <div className="leftHeader">
                  <DatePicker
                    onChange={handleDateChange}
                    format={"ddd, MMM DD"}
                    allowClear={false}
                    suffixIcon={<CalendarIcon />}
                  />
                  <div className="dateWrapper">
                    <Button
                      icon={<DateNavigatorLeft />}
                      type="text"
                      className="only_icon previous_btn"
                      // onClickCapture={() => {
                      //   const api = calendarRef.current.getApi();
                      //   // temporarily disable datesSet to avoid refetch clearing events
                      //   api.setOption("datesSet", () => undefined);
                      //   // manually update header range without touching weekStartDate
                      //   const start = dayjs(api.view.currentStart).subtract(
                      //     7,
                      //     "day"
                      //   );
                      //   const end = dayjs(api.view.currentEnd)
                      //     .subtract(7, "day")
                      //     .subtract(1, "day");
                      //   setCurrentWeekStartEnd({
                      //     start: start.format("YYYY-MM-DD"),
                      //     end: end.format("YYYY-MM-DD"),
                      //   });
                      //   setCurrentWeekRange(
                      //     `${start.format("MMM D")} – ${end.format("MMM D")}`
                      //   );
                      //   // restore datesSet after navigation
                      //   setTimeout(() => {
                      //     api.setOption("datesSet", handleDatesSet);
                      //   }, 0);
                      // }}
                      onClick={() =>
                        newChangesMade
                          ? (showChangesLeftPop(true),
                            addChangesLeftFunc({
                              prevBtn: true,
                              crossBtn: false,
                              nextBtn: false,
                              currentWeek: false,
                            }))
                          : calendarRef.current.getApi().prev()
                      }
                    />
                    <div className="datePickerWrapper">
                      <span className="date">{currentWeekRange}</span>
                    </div>
                    <Button
                      icon={<DateNavigatorRight />}
                      type="text"
                      className="only_icon next_btn"
                      onClick={() =>
                        newChangesMade
                          ? (showChangesLeftPop(true),
                            addChangesLeftFunc({
                              nextBtn: true,
                              crossBtn: false,
                              prevBtn: false,
                              currentWeek: false,
                            }))
                          : calendarRef.current.getApi().next()
                      }
                    />
                  </div>
                  {weekPosition !== "current" && (
                    <>
                      <Button
                        type="text"
                        onClick={() =>
                          newChangesMade
                            ? (showChangesLeftPop(true),
                              addChangesLeftFunc({
                                prevBtn: false,
                                crossBtn: false,
                                nextBtn: false,
                                currentWeek: true,
                              }))
                            : (() => {
                                const api = calendarRef.current?.getApi?.();
                                if (api) api.today();
                              })()
                        }
                        className="current_week_button"
                        style={{ marginLeft: 8 }}
                      >
                        <Tag className="current_week">Current week</Tag>
                      </Button>
                      <div className="totalHours">
                        <span>Scheduled / Available</span>
                      </div>
                    </>
                  )}
                  {weekPosition === "current" && (
                    <>
                      <Tag className="current_week">Current week</Tag>
                      <div className="totalHours">
                        Total hours:{" "}
                        <span>
                          {hoursData
                            ? calculateHours(
                                hoursData.reduce((sum, h) => sum + h.hours, 0) *
                                  crewData.length
                              )
                            : 0}
                          /{crewData.length * 8 * 5}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                {/* <div style={{ marginBottom: "20px", textAlign: "right" }}>
                  <Button onClick={triggerProcessLatLngs} className="mint-btn">
                    Recalculate Drive Times
                  </Button>
                </div> */}
              </div>
              <style>
                {`
                  /* Disable interactions on past days in timeGrid week */
                  .fc .fc-timegrid .fc-day-past .fc-timegrid-col,
                  .fc .fc-timegrid .fc-day-past .fc-timegrid-col-frame,
                  .fc .fc-timegrid .fc-day-past .fc-timegrid-events,
                  .fc .fc-timegrid .fc-day-past .fc-timegrid-bg,
                  .fc .fc-day-past .fc-event,
                  .fc .fc-event.fc-event-past {
                    pointer-events: none !important;
                  }

                  /* Optional: slightly dim past days */
                  .fc .fc-day-past {
                    background-color: #fafafa;
                  }
                `}
              </style>
              <div style={{ position: "relative" }}>
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                  initialView="timeGridWeek"
                  firstDay={1}
                  headerToolbar={false}
                  dayHeaderContent={(arg: any) => (
                    <CustomDayHeader
                      date={arg.date}
                      events={calendarEvents}
                      hoursData={hoursData}
                      crewData={crewData}
                      setLoading={setLoading}
                      loading={loading}
                      weekStartDate={weekStartDate}
                      selectedCrewValue={selectedCrewValue}
                      crewOptions={crewOptions}
                      crewArrayForCalendar={crewArrayForCalendar}
                      dayEvents={calendarEvents.filter(
                        (e) =>
                          e.start &&
                          dayjs(e.start).isSame(arg.date, "day") &&
                          e.type !== "weekend-bg" &&
                          e.type !== "drive" &&
                          e.type !== "crew-yard-start" &&
                          e.type !== "crew-yard-end"
                      )}
                      crewDrop={schedulingFormData?.data?.crews?.map(
                        (c: any) => ({
                          label: (
                            <>
                              <b>{c.name}</b> - {c.crewLeader?.first_name ?? ""}{" "}
                              {c.crewLeader?.last_name ?? ""}
                            </>
                          ),
                          value: c.id,
                        })
                      )}
                    />
                  )}
                  slotMinTime={slotStartTime || "08:00:00"}
                  slotMaxTime={slotEndTime || "19:00:00"}
                  slotDuration="00:30:00"
                  slotLabelInterval="01:00"
                  expandRows={false}
                  events={calendarEvents}
                  eventContent={(e) => (
                    <RenderEventContentSchedule
                      eventInfo={e}
                      setLoading={setLoading}
                      setSelectedVisitIds={setSelectedVisitIds}
                      selectedVisitIds={selectedVisitIds}
                    />
                  )}
                  eventReceive={handleEventReceive}
                  droppable={true}
                  allDaySlot={false}
                  editable={true}
                  eventDrop={handleEventDrop}
                  height="auto"
                  contentHeight="auto"
                  eventMinWidth={150}
                  // aspectRatio={1.8}
                  dayHeaderClassNames="custom-day-header"
                  dayCellClassNames="custom-day-cell"
                  datesSet={handleDatesSet}
                  eventOverlap={true}
                  slotEventOverlap={true}
                  // eventAllow={(dropInfo: any) => {
                  //   // Disallow drop if crew not selected
                  //   if (!selectedCrewValue) return false;

                  //   const start = dayjs(dropInfo.start);
                  //   const now = dayjs();

                  //   // Disallow past drops
                  //   // if (start.isBefore(now.startOf("day"))) return false;
                  //   if (start.isBefore(now)) return false;

                  //   // Disallow non-working days if configured
                  //   // if (
                  //   //   Array.isArray(settingsData?.workingDays) &&
                  //   //   settingsData.workingDays.length > 0
                  //   // ) {
                  //   //   const dayLabel = start.format("ddd");
                  //   //   if (!settingsData.workingDays.includes(dayLabel))
                  //   //     return false;
                  //   // }

                  //   return true;
                  // }}
                  nowIndicator={calendarEvents.some(
                    (event) =>
                      event.status === "IN_PROGRESS" &&
                      dayjs(event.start).isBefore(dayjs()) &&
                      dayjs(event.end).isAfter(dayjs())
                  )}
                />

                {!selectedCrewValue && (
                  <div className="dragIndicator emptySchedulerOverlay">
                    <img
                      src="/images/empty-icons/WorkSchedulerEmpty.svg"
                      alt=""
                    />
                    <div>
                      <h3
                        style={{
                          marginBottom: "8px",
                          color: "#474C4D",
                          fontSize: "20px",
                          width: "100%",
                          textAlign: "center",
                        }}
                      >
                        Select crew
                      </h3>
                      <p style={{ color: "#474C4D", fontSize: "16px" }}>
                        To view their schedule and manage their visits.
                      </p>
                    </div>
                    <Select
                      style={{ width: "280px" }}
                      suffixIcon={<SelectArrow />}
                      menuItemSelectedIcon={
                        <TickIcon style={{ color: "#00A07D" }} />
                      }
                      showSearch
                      // allowClear
                      placeholder="Select crew"
                      value={selectedCrewValue}
                      options={crewOptions}
                      onChange={(value) => {
                        if (setSelectedCrewValue) {
                          setSelectedCrewValue(value);
                          setCalendarEvents([]);
                          setVisitEvents([]);
                        }
                        if (setHoursData) {
                          setHoursData([]);
                        }
                      }}
                    />
                  </div>
                )}

                {selectedVisitIds?.length !== 0 || newChangesMade ? (
                  <div className="selection_bar2">
                    {selectedVisitIds?.length !== 0 && (
                      <div className="inside_selection_bar">
                        <div className="selected_count">
                          <Button
                            icon={<CrossIcon style={{ color: "white" }} />}
                            className="only_icon"
                            type="text"
                            onClick={() => setSelectedVisitIds([])}
                          />
                          {selectedVisitIds.length}{" "}
                          {`${
                            selectedVisitIds.length === 1 ? "visit" : "visits"
                          }`}{" "}
                          selected
                        </div>
                        <div
                          className="selection_buttons"
                          style={{
                            marginRight: newChangesMade ? 10 : 0,
                          }}
                        >
                          <Button
                            type="text"
                            icon={<DeleteIcon />}
                            onClick={() => unScheduleVisitsFnc()}
                          >
                            Unschedule
                          </Button>
                        </div>
                      </div>
                    )}
                    {newChangesMade && (
                      <>
                        <div
                          className="selection_buttons"
                          style={{
                            borderLeft: 0,
                            paddingLeft: 0,
                            marginLeft: 0,
                          }}
                        >
                          <Button
                            htmlType="button"
                            className="outlined-btn btn-sm"
                            type="primary"
                            size="large"
                            onClick={() => discardChanges()}
                            style={{
                              height: "44px",
                              marginRight: "10px",
                              borderRadius: "10px",
                            }}
                          >
                            <Image
                              src="/images/table/lost-icon.svg"
                              preview={false}
                            />
                            Discard changes
                          </Button>
                          <Button
                            size="large"
                            className="mint-btn"
                            onClick={() => publishSchedule()}
                            style={{ height: "44px", borderRadius: "10px" }}
                          >
                            <Image
                              src="/images/schedule/publish-icon.svg"
                              preview={false}
                            />{" "}
                            Publish schedule
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </FullPageLayout>
      <PopUp {...leftPopUp} />
    </Spin>
  );
}
